// ** React Imports
import { Fragment, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Offcanvas, Spinner } from 'react-bootstrap';
import { Line, Radar } from 'react-chartjs-2';
import { toast, Toaster } from 'react-hot-toast';
import Chart from 'chart.js';

// ** Reactstrap Imports
import { Row, Col, Container, Card } from "reactstrap";

// ** Icons Imports
import { Menu } from "react-feather"

// ** Services Imports
import { progressDimensionListService } from '../../../services/dimensionService';
import { indicatorListService } from '../../../services/indicatorService';
import { sectionQuestionService } from '../../../services/sectionService';

// Utility-Service
import { getUser, getToken } from "../../../utility/AuthService";
import ProgressQuestionPage from "../ProgressQuestionPage";
import ProgressSidebar from "../../components/layouts/ProgressSidebar";
import Header from "../../components/layouts/Navbar";
import Footer from '../../components/layouts/Footer'

//redux
import { selectLanguageUse } from '../../../redux/slices/language.Slice'
import CryptoJS from 'crypto-js';

const SMEReadiness = () => {
    const user = getUser()
    const [error, setError] = useState(null);
    const [decryptedSubId, setDecryptedSubId] = useState(null);
    const [loadingStates, setLoadingStates] = useState({
        dimensions: true,
        indicators: false,
        questions: false,
        sections: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [show, setShow] = useState(false);
    const [dimensionName, setDimensionName] = useState('');
    const [dimensionName_Malay, setDimensionName_Malay] = useState('');
    const [currentDimension, setCurrentDimension] = useState(null);
    const [currentDimensionID, setCurrentDimensionID] = useState(null);
    const [changeDimensionName, setChangeDimensionName] = useState(false);
    const [currentIndicator, setCurrentIndicator] = useState('');
    const [selectedIndicator, setSelectedIndicator] = useState({
        name: '',
        dimension: '',
        description: ''
    });
    const [allDimension, setAllDimension] = useState([]);
    const [allIndicator, setAllIndicator] = useState([]);
    const [allQuestions, setAllQuestions] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [currentDimensionIndex, setCurrentDimensionIndex] = useState(0);
    const languageUse = useSelector(selectLanguageUse || user?.language)
    const [scoreData, setScoreData] = useState({
        labels: [],
        datasets: []
    });
    
    const [radarData, setRadarData] = useState({
        labels: [],
        datasets: []
    });

    // Add logging function
    const logDataFlow = (step, data, error = null) => {
        console.log(`[Progress Component] ${step}:`, {
            timestamp: new Date().toISOString(),
            data,
            error
        });
    };

    // Add error handling function
    const handleError = (error, step) => {
        console.error(`[Progress Component] Error in ${step}:`, error);
        setError({
            message: error?.response?.data?.message || 'An error occurred',
            step
        });
        toast.error(error?.response?.data?.message || 'An error occurred');
    };

    // Update loading states
    const updateLoadingState = (key, value) => {
        setLoadingStates(prev => {
            const newStates = {
                ...prev,
                [key]: value
            };
            // Only set main loading to false when dimensions are loaded
            if (key === 'dimensions' && !value) {
                setIsLoading(false);
                logDataFlow('Initial loading complete', newStates);
            }
            return newStates;
        });
    };

    // Load dimensions
    const loadDimensions = async () => {
        if (!decryptedSubId) return;
        
        try {
            updateLoadingState('dimensions', true);
            const startTime = performance.now();
            logDataFlow('Starting dimensions load', { submissionId: decryptedSubId });

            const result = await progressDimensionListService({
                authToken: getToken(),
                submissionId: decryptedSubId,
                offset: 0,
                limit: 100,
                filter: "Active"
            });
            
            if (result?.data?.data?.list) {
                const dimensions = result.data.data.list;
                setAllDimension(dimensions);
                
                // Set initial dimension if none selected and dimensions exist
                if (!currentDimension && dimensions.length > 0) {
                    const firstDimension = dimensions[0];
                    if (firstDimension && firstDimension.name && firstDimension._id) {
                        setCurrentDimension(firstDimension.name);
                        setCurrentDimensionID(firstDimension._id);
                        setDimensionName(firstDimension.name);
                        setDimensionName_Malay(firstDimension.name_Malay || firstDimension.name);
                        setCurrentDimensionIndex(0);
                    } else {
                        logDataFlow('Invalid first dimension data', firstDimension);
                        toast.error('Invalid dimension data received');
                    }
                }
                
                logDataFlow('Dimensions loaded', { 
                    count: dimensions.length,
                    timeElapsed: performance.now() - startTime
                });
            } else {
                logDataFlow('No dimensions data received', result);
                toast.error('No dimensions data received');
            }
        } catch (error) {
            handleError(error, 'loading dimensions');
            logDataFlow('Dimensions load failed', null, error);
        } finally {
            updateLoadingState('dimensions', false);
        }
    };

    // Load indicators
    const loadIndicators = async () => {
        if (!currentDimension) return;
        
        try {
            updateLoadingState('indicators', true);
            const startTime = performance.now();
            logDataFlow('Starting indicators load', { dimension: currentDimension });

            const indicatorsResult = await indicatorListService({
                authToken: getToken(),
                dimension: currentDimension,
                limit: 100,
                offset: 0,
                filter: 'Active',
                submissionId: decryptedSubId
            });

            if (indicatorsResult?.data?.statusCode === 200) {
                setAllIndicator(indicatorsResult?.data?.data?.list || []);
                logDataFlow('Indicators loaded', { 
                    count: indicatorsResult?.data?.data?.list?.length,
                    timeElapsed: performance.now() - startTime
                });
            }
        } catch (error) {
            handleError(error, 'loading indicators');
            logDataFlow('Indicators load failed', null, error);
            const errorMessage = error?.response?.data?.message || 'Failed to load indicators';
            toast.error(errorMessage);
        } finally {
            updateLoadingState('indicators', false);
        }
    };

    // Load questions
    const loadQuestions = async () => {
        if (!currentIndicator) return;
        
        try {
            updateLoadingState('questions', true);
            const startTime = performance.now();
            logDataFlow('Starting questions load', { indicator: currentIndicator });

            const questionsResult = await sectionQuestionService({
                authToken: getToken(),
                indicatorId: currentIndicator,
                submissionId: decryptedSubId
            });

            if (questionsResult?.data?.statusCode === 200) {
                setAllQuestions(questionsResult?.data?.data?.list || []);
                logDataFlow('Questions loaded', { 
                    count: questionsResult?.data?.data?.list?.length,
                    timeElapsed: performance.now() - startTime
                });
            }
        } catch (error) {
            handleError(error, 'loading questions');
            logDataFlow('Questions load failed', null, error);
        } finally {
            updateLoadingState('questions', false);
        }
    };

    // Load sections
    const loadSections = async () => {
        if (!allQuestions.length) return;
        
        try {
            updateLoadingState('sections', true);
            const startTime = performance.now();
            logDataFlow('Starting sections load', { questionsCount: allQuestions.length });

            // Process questions to extract unique sections
            const sections = [...new Set(allQuestions.map(q => q.section))];
            setAllSections(sections);
            
            logDataFlow('Sections loaded', { 
                count: sections.length,
                timeElapsed: performance.now() - startTime
            });
        } catch (error) {
            handleError(error, 'loading sections');
            logDataFlow('Sections load failed', null, error);
        } finally {
            updateLoadingState('sections', false);
        }
    };

    // Decrypt submission ID
    useEffect(() => {
        const decryptId = () => {
            try {
                const id = window.location.pathname.split('/').pop();
                if (!id) {
                    throw new Error('No submission ID found');
                }
                const decodedId = decodeURIComponent(id);
                const decrypted = CryptoJS.AES.decrypt(decodedId, 'mySecretKey').toString(CryptoJS.enc.Utf8);
                setDecryptedSubId(decrypted);
                logDataFlow('ID Decrypted', { originalId: id, decrypted });
            } catch (error) {
                handleError(error, 'ID decryption');
                logDataFlow('ID Decryption Failed', null, error);
            }
        };

        decryptId();
    }, []);

    // Progressive loading effects
    useEffect(() => {
        if (decryptedSubId) {
            loadDimensions();
        }
    }, [decryptedSubId, currentDimension]);

    useEffect(() => {
        if (currentDimension) {
            loadIndicators();
        }
    }, [currentDimension]);

    useEffect(() => {
        if (currentIndicator) {
            loadQuestions();
        }
    }, [currentIndicator]);

    useEffect(() => {
        if (allQuestions.length > 0) {
            loadSections();
        }
    }, [allQuestions]);

    const data = {
        labels: [
            'Jan', 
            'Feb', 
            'Mar', 
            'Apr', 
            'May', 
            'Jun'
        ],
        datasets: [
            {
                label: 'Score',
                data: [65, 70, 75, 80, 85, 90],
                fill: true,
                borderColor: '#009EDB',
                backgroundColor: 'rgba(0, 158, 219, 0.1)',
                tension: 0.4
            }
        ]
    };

    const dimensions = [
        'Environment', 
        'Social', 
        'Governance', 
        'Demographics', 
        'Climate Action'
    ];

    // Chart options
    const lineOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'SME ESG Over Time'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100
            }
        }
    };

    const radarOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Dimension Scores'
            }
        },
        scales: {
            r: {
                beginAtZero: true,
                max: 100
            }
        }
    };

    // Update chart data when dimensions change
    useEffect(() => {
        if (allDimension && allDimension.length > 0) {
            // Example data - replace with actual data from your API
            setScoreData({
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [{
                    label: 'Score',
                    data: [65, 70, 75, 80, 85, 90],
                    fill: true,
                    borderColor: '#009EDB',
                    backgroundColor: 'rgba(0, 158, 219, 0.1)',
                    tension: 0.4
                }]
            });

            setRadarData({
                labels: allDimension.map(d => d.name),
                datasets: [{
                    label: 'Current Scores',
                    data: allDimension.map(() => Math.floor(Math.random() * 30) + 70), // Example data
                    fill: true,
                    backgroundColor: 'rgba(0, 158, 219, 0.2)',
                    borderColor: '#009EDB',
                    pointBackgroundColor: '#009EDB',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: '#009EDB'
                }]
            });
        }
    }, [allDimension]);

    // Navigation handlers
    const handlePreviousDimension = () => {
        if (currentDimensionIndex > 0 && allDimension[currentDimensionIndex - 1]) {
            const prevDimension = allDimension[currentDimensionIndex - 1];
            setCurrentDimension(prevDimension.name);
            setCurrentDimensionID(prevDimension._id);
            setCurrentDimensionIndex(currentDimensionIndex - 1);
            setAllIndicator([]);
            setAllQuestions([]);
            setAllSections([]);
        }
    };

    const handleNextDimension = () => {
        if (currentDimensionIndex < allDimension.length - 1 && allDimension[currentDimensionIndex + 1]) {
            const nextDimension = allDimension[currentDimensionIndex + 1];
            setCurrentDimension(nextDimension.name);
            setCurrentDimensionID(nextDimension._id);
            setCurrentDimensionIndex(currentDimensionIndex + 1);
            setAllIndicator([]);
            setAllQuestions([]);
            setAllSections([]);
        }
    };

    const handleDimensionSelect = (dimension) => {
        if (dimension && dimension.name && dimension._id) {
            setCurrentDimension(dimension.name);
            setCurrentDimensionID(dimension._id);
            setDimensionName(dimension.name);
            setDimensionName_Malay(dimension.name_Malay || dimension.name);
            setAllIndicator([]);
            setAllQuestions([]);
            setAllSections([]);
            setShow(false); // Close the sidebar after selection
        } else {
            logDataFlow('Invalid dimension selected', dimension);
            toast.error('Invalid dimension data received');
        }
    };

    const handleSidebar = () => {
        setShow(!show);
    }

    return <>
        <div>
            <Toaster />
        </div>

        <Header />

        {error && (
            <div className="alert alert-danger m-3" role="alert">
                <strong>Error in {error.step}:</strong> {error.message}
            </div>
        )}

        <Container fluid>
            <Row>
                <Col xs={12} className="px-0">
                    <div className="d-flex align-items-center justify-content-between px-4 py-2">
                        <div className="d-flex align-items-center">
                            <Menu className="cursor-pointer me-2" onClick={() => setShow(true)} />
                            <h4 className="mb-0">{languageUse === 'English' ? 'SME READINESS Assessment' : 'Évaluation de préparation des PME'}</h4>
                        </div>
                        {allDimension && allDimension.length > 1 && (
                            <div className="d-flex align-items-center">
                                <button 
                                    className="btn btn-outline-primary me-2"
                                    onClick={handlePreviousDimension}
                                    disabled={currentDimensionIndex === 0}
                                >
                                    Previous
                                </button>
                                <span className="me-2">
                                    Dimension {currentDimensionIndex + 1} of {allDimension.length}
                                </span>
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={handleNextDimension}
                                    disabled={currentDimensionIndex === allDimension.length - 1}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xs={12} className="px-0">
                    {loadingStates.dimensions ? (
                        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '70vh' }}>
                            <Spinner animation="grow" className="mb-3" />
                            <div className="text-center">
                                <p className="mb-2">Loading dimensions...</p>
                            </div>
                        </div>
                    ) : currentDimensionID ? (
                        <ProgressQuestionPage
                            dimensionName={dimensionName}
                            dimensionName_Malay={dimensionName_Malay}
                            currentDimension={currentDimension}
                            dimensionID={currentDimensionID}
                            changeDimensionName={changeDimensionName}
                            currentIndicator={currentIndicator}
                            selectedIndicator={selectedIndicator}
                            setDimensionName={setDimensionName}
                            setDimensionName_Malay={setDimensionName_Malay}
                            setCurrentDimension={setCurrentDimension}
                            setDimensionID={setCurrentDimensionID}
                            setChangeDimensionName={setChangeDimensionName}
                            setCurrentIndicator={setCurrentIndicator}
                            setSelectedIndicator={setSelectedIndicator}
                            isLoading={loadingStates}
                            allDimension={allDimension}
                            allIndicator={allIndicator}
                            allQuestions={allQuestions}
                            allSections={allSections}
                        />
                    ) : (
                        <div className="text-center p-5">
                            <div className="alert alert-warning">
                                No dimensions available. Please try refreshing the page.
                            </div>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>

        <Footer />

        <Offcanvas show={show} onHide={() => setShow(false)}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{languageUse === 'English' ? 'Navigation' : 'Navigation'}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <ProgressSidebar
                    dimensions={allDimension}
                    currentDimension={currentDimensionID}
                    onDimensionSelect={handleDimensionSelect}
                />
            </Offcanvas.Body>
        </Offcanvas>
    </>;
};

export default SMEReadiness;
